import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Save, Eye, EyeOff, Loader2,
  Layout, Info, Star, Video, Images, Settings,
} from 'lucide-react';
import { toast } from 'sonner';

import HeroEditor from '../components/landing-editor/HeroEditor';
import AboutEditor from '../components/landing-editor/AboutEditor';
import FeaturesEditor from '../components/landing-editor/FeaturesEditor';
import TestimonialsEditor from '../components/landing-editor/TestimonialsEditor';
import VideosEditor from '../components/landing-editor/VideosEditor';
import GalleryEditor from '../components/landing-editor/GalleryEditor';
import SettingsEditor from '../components/landing-editor/SettingsEditor';
import ThemeEditor from '../components/landing-editor/ThemeEditor';
import SectionOrderEditor from '../components/landing-editor/SectionOrderEditor';

const TABS = [
  { key: 'hero', label: 'البطل', icon: Layout },
  { key: 'about', label: 'نبذة عن المبادرة', icon: Info },
  { key: 'features', label: 'المميزات', icon: Star },
  { key: 'testimonials', label: 'آراء المشاركين', icon: Star },
  { key: 'videos', label: 'فيديوهات', icon: Video },
  { key: 'gallery', label: 'معرض الصور', icon: Images },
  { key: 'order', label: 'ترتيب الأقسام', icon: Layout },
  { key: 'settings', label: 'إعدادات عامة', icon: Settings },
  { key: 'theme', label: 'الألوان', icon: Settings },
];

const AdminLandingPage = () => {
  const [sections, setSections] = useState({});
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [themeColors, setThemeColors] = useState({ primary: '#C19A6B', accent: '#5D4037', secondary: '#3A5F0B' });
  const [savingTheme, setSavingTheme] = useState(false);

  useEffect(() => { loadSections(); loadTheme(); }, []);

  const loadTheme = async () => {
    try { const res = await api.get('/public/theme'); setThemeColors(res.data); } catch {}
  };

  const saveTheme = async () => {
    setSavingTheme(true);
    try {
      await api.put('/admin/theme', themeColors);
      const hexToRgb = (hex) => {
        const h = hex.replace('#', '');
        return `${parseInt(h.substring(0,2),16)} ${parseInt(h.substring(2,4),16)} ${parseInt(h.substring(4,6),16)}`;
      };
      const root = document.documentElement;
      root.style.setProperty('--color-primary', hexToRgb(themeColors.primary));
      root.style.setProperty('--color-accent', hexToRgb(themeColors.accent));
      root.style.setProperty('--color-secondary', hexToRgb(themeColors.secondary));
      toast.success('تم تحديث الألوان بنجاح');
    } catch { toast.error('فشل تحديث الألوان'); }
    finally { setSavingTheme(false); }
  };

  const loadSections = async () => {
    try { const res = await api.get('/admin/landing'); setSections(res.data); }
    catch { toast.error('فشل تحميل البيانات'); }
    finally { setLoading(false); }
  };

  const getData = (key) => sections[key]?.data || {};
  const isVisible = (key) => sections[key]?.is_visible !== false;
  const setData = (key, data) => setSections(prev => ({ ...prev, [key]: { ...prev[key], data } }));
  const toggleVisible = (key) => setSections(prev => ({ ...prev, [key]: { ...prev[key], is_visible: !isVisible(key) } }));

  const saveSection = async (key) => {
    setSaving(true);
    try {
      await api.put(`/admin/landing/${key}`, { data: getData(key), is_visible: isVisible(key) });
      toast.success('تم الحفظ');
    } catch { toast.error('فشل الحفظ'); }
    finally { setSaving(false); }
  };

  const uploadImage = async (file, onSuccess) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/landing-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      onSuccess(url);
      toast.success('تم رفع الصورة');
    } catch (err) { toast.error(err.response?.data?.detail || 'فشل الرفع'); }
    finally { setUploading(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-16 h-16 animate-spin text-primary" /></div>;

  const renderTab = () => {
    const props = { data: getData(activeTab), setData: (d) => setData(activeTab, d), uploadImage, uploading };
    switch (activeTab) {
      case 'hero': return <HeroEditor {...props} />;
      case 'about': return <AboutEditor {...props} />;
      case 'features': return <FeaturesEditor {...props} />;
      case 'testimonials': return <TestimonialsEditor {...props} />;
      case 'videos': return <VideosEditor {...props} />;
      case 'gallery': return <GalleryEditor {...props} />;
      case 'order': return <SectionOrderEditor sections={sections} />;
      case 'settings': return <SettingsEditor {...props} />;
      case 'theme': return <ThemeEditor themeColors={themeColors} setThemeColors={setThemeColors} saveTheme={saveTheme} savingTheme={savingTheme} />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-landing-page">
      <div>
        <h1 className="text-4xl font-bold text-accent">إدارة صفحة الهبوط</h1>
        <p className="text-muted-foreground text-lg mt-1">تحرير محتوى الصفحة الرئيسية للزوار</p>
      </div>

      <div className="flex gap-6">
        <div className="w-48 shrink-0 space-y-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`w-full flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === tab.key ? 'bg-primary text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}
                data-testid={`tab-${tab.key}`}>
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-accent">{TABS.find(t => t.key === activeTab)?.label}</h2>
              {activeTab !== 'theme' && activeTab !== 'order' && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => toggleVisible(activeTab)} data-testid={`toggle-visible-${activeTab}`}>
                    {isVisible(activeTab) ? <><Eye className="w-4 h-4 ml-1" /> ظاهر</> : <><EyeOff className="w-4 h-4 ml-1" /> مخفي</>}
                  </Button>
                  <Button size="sm" onClick={() => saveSection(activeTab)} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid={`save-${activeTab}`}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 ml-1" /> حفظ</>}
                  </Button>
                </div>
              )}
            </div>
            {renderTab()}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminLandingPage;
